import React from 'react';
import { Card } from "@/components/ui/card";
import { useSleepSchedule } from './SleepScheduleContext';
import { TrendingUp, Clock, CheckCircle2, AlertTriangle } from 'lucide-react';

export function AdherenceMetrics() {
  const { scheduleItems } = useSleepSchedule();

  // Calculate adherence metrics
  const totalItems = scheduleItems.length;
  const completedItems = scheduleItems.filter(item => item.actualStartTime).length;
  const onTimeItems = scheduleItems.filter(item => item.status === 'on-time').length;
  const lateItems = scheduleItems.filter(item => item.status === 'late').length;
  const missedItems = scheduleItems.filter(item => item.status === 'missed').length;

  const adherenceRate = totalItems > 0 ? Math.round((onTimeItems / totalItems) * 100) : 0;
  const completionRate = totalItems > 0 ? Math.round((completedItems / totalItems) * 100) : 0;

  // Calculate average variance for completed items
  const itemsWithVariance = scheduleItems.filter(item => item.actualStartTime);
  const totalVariance = itemsWithVariance.reduce((sum, item) => {
    const planned = new Date(item.scheduledStartTime);
    const actual = new Date(item.actualStartTime!);
    return sum + Math.abs((actual.getTime() - planned.getTime()) / (1000 * 60));
  }, 0);
  const avgVariance = itemsWithVariance.length > 0 
    ? Math.round(totalVariance / itemsWithVariance.length) 
    : 0;

  if (totalItems === 0) return null;

  const getCompletionMessage = () => {
    if (completionRate === 100) return "Perfect! All activities logged";
    if (completionRate >= 75) return "Good tracking progress";
    if (completionRate >= 50) return "Keep logging activities";
    return "More logging needed";
  };

  const getAdherenceMessage = () => {
    if (adherenceRate >= 80) return "Excellent schedule adherence";
    if (adherenceRate >= 60) return "Good consistency";
    if (adherenceRate >= 40) return "Schedule may need adjustment";
    return "Consider revising schedule";
  };

  const getVarianceMessage = () => {
    if (avgVariance <= 30) return "Excellent timing consistency";
    if (avgVariance <= 60) return "Minor timing variations";
    return "Significant schedule drift";
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <Card className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-blue-700 font-medium">Completion Rate</p>
            <p className="text-2xl font-bold text-blue-900">{completionRate}%</p>
            <p className="text-xs text-blue-600 mt-1">{completedItems} of {totalItems} activities logged</p>
            <p className="text-xs text-blue-700 font-medium mt-2">{getCompletionMessage()}</p>
          </div>
          <CheckCircle2 className="h-8 w-8 text-blue-600" />
        </div>
      </Card>

      <Card className="p-4 bg-gradient-to-br from-green-50 to-green-100 border-green-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-green-700 font-medium">On-Time Rate</p>
            <p className="text-2xl font-bold text-green-900">{adherenceRate}%</p>
            <p className="text-xs text-green-600 mt-1">{onTimeItems} started within 30 minutes of plan</p>
            <p className="text-xs text-green-700 font-medium mt-2">{getAdherenceMessage()}</p>
          </div>
          <TrendingUp className="h-8 w-8 text-green-600" />
        </div>
      </Card>

      <Card className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-amber-700 font-medium">Average Time Difference</p>
            <p className="text-2xl font-bold text-amber-900">{avgVariance} {avgVariance === 1 ? 'minute' : 'minutes'}</p>
            <p className="text-xs text-amber-600 mt-1">
              {lateItems > 0 ? `${lateItems} ${lateItems === 1 ? 'activity was' : 'activities were'} late` : 'All on time or early'}
            </p>
            <p className="text-xs text-amber-700 font-medium mt-2">
              {getVarianceMessage()}
            </p>
          </div>
          <Clock className="h-8 w-8 text-amber-600" />
        </div>
      </Card>

      <Card className="p-4 bg-gradient-to-br from-red-50 to-red-100 border-red-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-red-700 font-medium">Missed Activities</p>
            <p className="text-2xl font-bold text-red-900">{missedItems}</p>
            <p className="text-xs text-red-600 mt-1">
              {missedItems === 0 ? 'No activities were skipped' : `${missedItems} ${missedItems === 1 ? 'activity was' : 'activities were'} not logged`}
            </p>
            <p className="text-xs text-red-700 font-medium mt-2">
              {missedItems === 0 ? 'Perfect tracking!' : 'Log all activities for accurate insights'}
            </p>
          </div>
          <AlertTriangle className="h-8 w-8 text-red-600" />
        </div>
      </Card>
    </div>
  );
}
